/**
 * viewsImage.js — composite the 6 orthographic renders into ONE landscape PNG on a
 * dark background, matching the customer sample (for teams who want an image, not a
 * PDF). Layout: 3 cols × 2 rows, axis-paired [A/P | RL/LL | S/I]; abbreviation label
 * top-right per cell; tiled "Osteo3d ®" watermark; date (top-left),
 * surgeon (bottom-left), "Osteo3d®" (bottom-centre), Case/Product ID (bottom-right).
 * All text in Mulish (loaded via the FontFace API).
 *
 * The renders must be CLEAN (renderReport with cornerLabels:false, watermark:false)
 * and on the SAME charcoal background as this canvas so they sit seamlessly.
 */

export const IMAGE_BG = '#2b2b2b' // dark charcoal — pass as renderReport background too

const GRID = [['A', 'RL', 'S'], ['P', 'LL', 'I']]

async function loadMulish() {
  try {
    if (document.fonts.check('700 40px Mulish')) return
    const reg = new FontFace('Mulish', 'url(/fonts/Mulish-Regular.ttf)')
    const bold = new FontFace('Mulish', 'url(/fonts/Mulish-Bold.ttf)', { weight: '700' })
    await Promise.all([reg.load(), bold.load()])
    document.fonts.add(reg); document.fonts.add(bold)
  } catch { /* fall back to the default sans-serif */ }
}

function b64ToImg(b64) {
  return new Promise((res) => {
    if (!b64) { res(null); return }
    const im = new Image()
    im.onload = () => res(im); im.onerror = () => res(null)
    im.src = b64.startsWith('data:') ? b64 : `data:image/png;base64,${b64}`
  })
}

// "Osteo3d®" centred at (cx,y) with the "3d" in brand cyan (matches the samples).
function drawBrand(ctx, cx, y, size, FONT) {
  ctx.textBaseline = 'alphabetic'; ctx.font = `bold ${size}px ${FONT}`
  const parts = [['Osteo', '#e8e8e8'], ['3d', '#1E96C8'], ['®', '#e8e8e8']]
  const widths = parts.map(([t]) => ctx.measureText(t).width)
  const total = widths.reduce((a, b) => a + b, 0)
  let x = cx - total / 2
  ctx.textAlign = 'left'
  parts.forEach(([t, c], i) => { ctx.fillStyle = c; ctx.fillText(t, x, y); x += widths[i] })
}

/**
 * @param {object} o { views:{code:b64}, date, surgeonName, caseId }
 * @returns {Promise<Blob>} PNG blob
 */
export async function buildViewsImage({ views = {}, date = '', surgeonName = '', caseId = '', note = '' }) {
  await loadMulish()
  const FONT = 'Mulish, sans-serif'
  const CELL_W = 820, CELL_H = 640, COLS = 3, ROWS = 2
  const MX = 44, TOP = 100, BOTTOM = 120
  const W = MX * 2 + COLS * CELL_W
  const H = TOP + ROWS * CELL_H + BOTTOM
  const gx = MX, gy = TOP

  const canvas = document.createElement('canvas')
  canvas.width = W; canvas.height = H
  const ctx = canvas.getContext('2d')
  ctx.fillStyle = IMAGE_BG; ctx.fillRect(0, 0, W, H)

  // renders (aspect-fit each square render into its cell)
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      const img = await b64ToImg(views[GRID[r][c]])
      if (!img) continue
      const x = gx + c * CELL_W, y = gy + r * CELL_H
      const s = Math.min(CELL_W / img.naturalWidth, CELL_H / img.naturalHeight)
      const iw = img.naturalWidth * s, ih = img.naturalHeight * s
      ctx.drawImage(img, x + (CELL_W - iw) / 2, y + (CELL_H - ih) / 2, iw, ih)
    }
  }

  // tiled faint diagonal watermark across the whole image
  ctx.save()
  ctx.globalAlpha = 0.06
  ctx.fillStyle = '#cfd4d8'
  ctx.font = `40px ${FONT}`
  ctx.textAlign = 'left'; ctx.textBaseline = 'middle'
  ctx.translate(W / 2, H / 2); ctx.rotate(-20 * Math.PI / 180); ctx.translate(-W / 2, -H / 2)
  for (let y = -H * 0.3; y < H * 1.3; y += 112) {
    for (let x = -W * 0.3; x < W * 1.3; x += 300) ctx.fillText('Osteo3d ®', x, y)
  }
  ctx.restore()

  // grid border + dividers
  ctx.strokeStyle = '#8a8f95'; ctx.lineWidth = 1.5
  ctx.strokeRect(gx, gy, COLS * CELL_W, ROWS * CELL_H)
  for (let c = 1; c < COLS; c++) { ctx.beginPath(); ctx.moveTo(gx + c * CELL_W, gy); ctx.lineTo(gx + c * CELL_W, gy + ROWS * CELL_H); ctx.stroke() }
  for (let r = 1; r < ROWS; r++) { ctx.beginPath(); ctx.moveTo(gx, gy + r * CELL_H); ctx.lineTo(gx + COLS * CELL_W, gy + r * CELL_H); ctx.stroke() }

  // per-cell abbreviation label (top-right)
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      const code = GRID[r][c]
      const x = gx + c * CELL_W, y = gy + r * CELL_H
      ctx.fillStyle = '#ffffff'; ctx.font = `bold 42px ${FONT}`
      ctx.textAlign = 'right'; ctx.textBaseline = 'top'
      ctx.fillText(code, x + CELL_W - 16, y + 12)
    }
  }

  // header / footer text
  ctx.fillStyle = '#e8e8e8'; ctx.textBaseline = 'alphabetic'
  ctx.font = `34px ${FONT}`; ctx.textAlign = 'left'
  ctx.fillText(date, MX + 6, TOP - 32)                       // date, top-left
  const rowY = H - 64, brandY = H - 22
  ctx.font = `38px ${FONT}`; ctx.textAlign = 'left'
  ctx.fillText(surgeonName, MX + 40, rowY)                   // surgeon, bottom-left
  ctx.textAlign = 'center'
  ctx.fillText(note, W / 2, rowY)                            // additional note, bottom-centre
  ctx.textAlign = 'right'
  ctx.fillText(caseId, W - MX - 40, rowY)                    // case/product id, bottom-right
  drawBrand(ctx, W / 2, brandY, 42, FONT)                    // brand (cyan 3d), below note

  return new Promise((res) => canvas.toBlob((b) => res(b), 'image/png'))
}

/**
 * ONE landscape PNG for a SINGLE view (team wants per-view files alongside the 6-view
 * composite). Same detail set as the composite: charcoal bg, thin white border, tiled
 * watermark, view label (abbrev + full name) top-right, footer date(TL)/surgeon(BL)/
 * note(BC)/`${caseId}_a${index}`(BR)/Osteo3d®(below). `render` = a CLEAN square PNG
 * (renderReport cornerLabels:false, watermark:false, background charcoal).
 * @returns {Promise<Blob>}
 */
export async function buildSingleViewImage({ render, code = '', label = '', index = 1, date = '', surgeonName = '', caseId = '', note = '' }) {
  await loadMulish()
  const FONT = 'Mulish, sans-serif'
  const W = 1600, H = 830, B = 14
  const canvas = document.createElement('canvas')
  canvas.width = W; canvas.height = H
  const ctx = canvas.getContext('2d')
  ctx.fillStyle = IMAGE_BG; ctx.fillRect(0, 0, W, H)

  // render — aspect-fit centred in the content area (below the top, above the footer)
  const RT = 22, RB = 96, RS = 34
  const areaX = RS, areaY = RT, areaW = W - 2 * RS, areaH = H - RT - RB
  const img = await b64ToImg(render)
  if (img) {
    const s = Math.min(areaW / img.naturalWidth, areaH / img.naturalHeight)
    const iw = img.naturalWidth * s, ih = img.naturalHeight * s
    ctx.drawImage(img, areaX + (areaW - iw) / 2, areaY + (areaH - ih) / 2, iw, ih)
  }

  // tiled diagonal watermark across the whole image
  ctx.save()
  ctx.globalAlpha = 0.06; ctx.fillStyle = '#cfd4d8'
  ctx.font = `32px ${FONT}`; ctx.textAlign = 'left'; ctx.textBaseline = 'middle'
  ctx.translate(W / 2, H / 2); ctx.rotate(-20 * Math.PI / 180); ctx.translate(-W / 2, -H / 2)
  for (let y = -H * 0.3; y < H * 1.3; y += 96) {
    for (let x = -W * 0.3; x < W * 1.3; x += 250) ctx.fillText('Osteo3d ®', x, y)
  }
  ctx.restore()

  // thin white border
  ctx.strokeStyle = '#e8e8e8'; ctx.lineWidth = 2
  ctx.strokeRect(B, B, W - 2 * B, H - 2 * B)

  ctx.fillStyle = '#e8e8e8'; ctx.textBaseline = 'alphabetic'
  ctx.font = `28px ${FONT}`; ctx.textAlign = 'left'
  ctx.fillText(date, B + 22, B + 42)                          // date, top-left
  // view label top-right: abbreviation + full name
  if (code) {
    ctx.textAlign = 'right'; ctx.fillStyle = '#ffffff'
    ctx.font = `bold 40px ${FONT}`; ctx.fillText(code, W - B - 22, B + 48)
    if (label) { ctx.font = `24px ${FONT}`; ctx.fillStyle = '#e8e8e8'; ctx.fillText(label, W - B - 22, B + 82) }
  }
  // footer: surgeon (BL) · note (BC) · caseId_aN (BR) · brand (below)
  const rowY = H - 54, brandY = H - 18
  ctx.fillStyle = '#e8e8e8'; ctx.font = `28px ${FONT}`
  ctx.textAlign = 'left'; ctx.fillText(surgeonName, B + 26, rowY)
  ctx.textAlign = 'center'; ctx.fillText(note, W / 2, rowY)
  ctx.textAlign = 'right'; ctx.fillText(caseId ? `${caseId}_a${index}` : `_a${index}`, W - B - 26, rowY)
  drawBrand(ctx, W / 2, brandY, 30, FONT)

  return new Promise((res) => canvas.toBlob((b) => res(b), 'image/png'))
}
